import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { renderSbIcon } from '../IconPickerField';
import { getf, sfHeadline, sfSub } from './sectionHelpers';

interface ServiceItem {
  icon?: string;
  title?: string;
  heading?: string;
  name?: string;
  description?: string;
  body?: string;
  text?: string;
  url?: string;
  cta_label?: string;
  cta_url?: string;
}

const ServicesCards: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const variant = c.variant || section.variant || 'icon-top';

  // Python falls back: services → cards → items
  const services = (c.services || c.cards || c.items || []) as ServiceItem[];

  // Grid columns from variant or explicit control
  const cols = c.columns || (variant === 'two-col' ? 2 : variant === 'four-col' ? 4 : 3);

  return (
    <section className="sb-render sb-render-services-cards" style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          {c.section_label && (
            <span style={{
              display: 'block', fontSize: '0.875rem', fontWeight: 600,
              color: 'var(--brand-primary, #2563eb)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 8,
            }}>
              {c.section_label}
            </span>
          )}
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', marginBottom: 12, letterSpacing: '-0.025em', fontWeight: 700 }}
          >
            {sfHeadline(c, 'Our Services')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)', maxWidth: 640, margin: '0 auto' }}
          >
            {sfSub(c, '')}
          </InlineEditText>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 24 }}>
          {services.map((s, i) => {
            // Python field-name fallback chain for per-item fields
            const title = getf(s as any, 'title', 'heading', 'name') || `Service ${i + 1}`;
            const desc  = getf(s as any, 'description', 'body', 'text') || '';
            const ctaLabel = getf(s as any, 'cta_label', 'cta_text', 'link_text') || 'Learn more';
            const ctaUrl   = getf(s as any, 'cta_url', 'url', 'link') || '#';
            const iconName = s.icon || '';

            return (
              <div key={i} style={{
                background: 'var(--brand-bg, #fff)',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 'var(--brand-card-radius, 12px)',
                padding: 32,
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: variant === 'icon-left' ? 'row' : 'column',
                gap: variant === 'icon-left' ? 20 : 0,
                alignItems: variant === 'icon-left' ? 'flex-start' : 'stretch',
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: 'rgba(37,99,235,0.1)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: variant === 'icon-left' ? 0 : 16,
                  fontSize: '1.25rem', color: 'var(--brand-primary, #2563eb)',
                  flexShrink: 0,
                }}>
                  {renderSbIcon(iconName, 24) ?? '★'}
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', marginBottom: 8 }}>{title}</h3>
                  {desc && (
                    <p style={{ color: 'var(--brand-text-light, #64748b)', lineHeight: 1.6, marginBottom: 12 }}>
                      {desc}
                    </p>
                  )}
                  <a href={ctaUrl} style={{ color: 'var(--brand-primary, #2563eb)', fontWeight: 600, textDecoration: 'none' }}>
                    {ctaLabel} →
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesCards;
